import { useState, memo } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';
import DashboardJobCard from './DashboardJobCard';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween.js';
dayjs.extend(isBetween);

const HotJobsCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		hotSearchedJobs = [],
		currentUser: propCurrentUser,
	}) => {
		const cardData = card.data || {};
		const title = cardData.title || 'Hot Jobs';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#666666';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;
		const showHotJobsOnly = cardData.showHotJobsOnly !== false;
		const reduxCurrentUser = useSelector((state) => state?.user?.currentUser);
		const currentUser = propCurrentUser || reduxCurrentUser;
		const contactIncentiveBonus =
			currentUser?.company?.contactIncentiveBonus || false;

		const columns = card.width || 1;
		const rows = card.height || 1;
		const jobsPerPage = columns * rows;

		const allJobs =
			hotSearchedJobs.length > 0 || !isEditMode ? hotSearchedJobs : [];
		const filteredJobs = showHotJobsOnly
			? allJobs.filter((job) => job.isHotJob === true)
			: allJobs;

		const [currentPage, setCurrentPage] = useState(0);
		const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
		const startIndex = currentPage * jobsPerPage;
		const visibleJobs = filteredJobs.slice(
			startIndex,
			startIndex + jobsPerPage
		);

		const goToNextPage = () => {
			if (currentPage < totalPages - 1) {
				setCurrentPage(currentPage + 1);
			}
		};

		const goToPreviousPage = () => {
			if (currentPage > 0) {
				setCurrentPage(currentPage - 1);
			}
		};

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const allMultiLingualData = useSelector(
			(state) => state.dashboard.allMultiLingualData
		);
		const allowSelfReferrals = currentUser?.company?.allowSelfReferrals;
		const isExtendedUser = currentUser?.role === 'extendedUser';

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '12px',
					}}
				>
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							margin: 0,
							color: textColor,
							fontFamily: 'Nunito Sans, sans-serif',
						}}
					>
						{title}
					</h4>

					{totalPages > 1 && (
						<div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
							<button
								onClick={goToPreviousPage}
								disabled={currentPage === 0}
								style={{
									padding: '4px 8px',
									border: `1px solid ${borderColor}`,
									borderRadius: '4px',
									background: currentPage === 0 ? '#f5f5f5' : '#ffffff',
									cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
									display: 'flex',
									alignItems: 'center',
									opacity: currentPage === 0 ? 0.5 : 1,
								}}
							>
								<FiChevronLeft size={16} color={textColor} />
							</button>
							<span
								style={{
									fontSize: '11px',
									color: textColor,
									fontFamily: 'Nunito Sans, sans-serif',
								}}
							>
								{currentPage + 1} / {totalPages}
							</span>
							<button
								onClick={goToNextPage}
								disabled={currentPage >= totalPages - 1}
								style={{
									padding: '4px 8px',
									border: `1px solid ${borderColor}`,
									borderRadius: '4px',
									background:
										currentPage >= totalPages - 1 ? '#f5f5f5' : '#ffffff',
									cursor:
										currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
									display: 'flex',
									alignItems: 'center',
									opacity: currentPage >= totalPages - 1 ? 0.5 : 1,
								}}
							>
								<FiChevronRight size={16} color={textColor} />
							</button>
						</div>
					)}
				</div>

				<div
					className="flex-1"
					style={{
						minHeight: 0,
						overflow: 'auto',
					}}
				>
					{visibleJobs.length > 0 ? (
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
								gap: '16px',
								height: '100%',
							}}
						>
							{visibleJobs.map((job) => (
								<DashboardJobCard
									key={job?.id || 'skip'}
									allMultiLingualData={allMultiLingualData}
									isExtendedUser={isExtendedUser}
									jobId={job?.id || 'skip'}
									job={job}
									contactIncentiveBonus={contactIncentiveBonus}
									contacts={[]}
									contactsNextToken={true}
									currentUser={currentUser}
									allowSelfReferrals={allowSelfReferrals}
									currentCurrencyRate={0}
									currentCurrencySymbol={'$'}
									currentUserCompany={currentUser?.company}
								/>
							))}
						</div>
					) : (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								textAlign: 'center',
								color: textColor,
								opacity: 0.6,
								fontFamily: 'Nunito Sans, sans-serif',
								height: '100%',
							}}
						>
							No jobs available
						</div>
					)}
				</div>
			</div>
		);
	}
);

HotJobsCard.displayName = 'HotJobsCard';

export default HotJobsCard;
